/**
 * VoiceButton — botão "VOZ" no Header IA do web /ia (Fase Voice S2).
 *
 * Estilo retro MS-DOS espelhado dos demais botões IA (PERFIL/SOAP/TEMPLATES/PLANTÃO).
 *
 * Estados:
 *   - free/plantonista: cadeado 🔒 sobreposto, glow reduzido — click abre upgrade modal
 *   - especialista/unlimited + idle: glow normal — click inicia pareamento
 *   - especialista/unlimited + active: glow forte verde — sessão em curso
 *
 * forwardRef permite que pai foque ou ancore tooltip nativo.
 */
import { forwardRef, type CSSProperties } from 'react';
import { LedPanel, type LedVariant } from './LedPanel';
import type { VoiceState } from '../hooks/useVoicePairing';
import type { UserTier } from '../types';

/** Tier do user (mesmo conjunto de UserTier — null = ainda carregando). */
export type VoiceButtonTier = UserTier | null;

export interface VoiceButtonProps {
  /** Tier atual do user. */
  readonly tier: VoiceButtonTier;
  /** true quando sessão voice ativa (state !== 'idle' && !== 'error'). */
  readonly active: boolean;
  /**
   * Estado completo da máquina voice — mapeia LED variant (verde/amarelo/
   * vermelho/off). Default 'idle' garante compat com chamadas antigas.
   */
  readonly voiceState?: VoiceState;
  /**
   * Click handler — pai decide se abre paywall (free/plantonista) ou
   * inicia pareamento (especialista/unlimited).
   */
  readonly onClick: () => void;
  /** Função de tradução do useTranslations. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

/**
 * Helper puro — decide se exibe cadeado.
 *
 * FIX 24/05/2026 (Dr. Lu): free user com trial ativo PODE usar voz
 * (backend libera via isTrialActive — voice/tier-gate.ts:68).
 * Antes o frontend bloqueava antes de tentar, então trial nunca
 * chegava no backend. Agora: free clica → backend valida → se trial
 * expirou/zerou volta tier_blocked e UI mostra paywall normal.
 *
 * Plantonista continua bloqueado client-side pra forçar upgrade
 * (não tem trial ativo, sempre cai em tier_blocked).
 */
export function shouldShowLock(tier: VoiceButtonTier): boolean {
  return tier === 'plantonista';
}

/**
 * Helper puro — escolhe a chave i18n do tooltip pra cada estado do botão.
 * (i18n vive no componente; este helper só decide qual chave usar.)
 */
export function getVoiceButtonTooltipKey(
  tier: VoiceButtonTier,
  active: boolean,
): 'voice.button.tooltip_locked' | 'voice.button.tooltip_active' | 'voice.button.tooltip_idle' {
  if (shouldShowLock(tier)) return 'voice.button.tooltip_locked';
  if (active) return 'voice.button.tooltip_active';
  return 'voice.button.tooltip_idle';
}

/**
 * Helper puro — mapeia VoiceState → LedVariant Knight Rider.
 *
 *   idle / tier_blocked        → off (3 LEDs apagados)
 *   requesting / waiting       → amber (KR amarelo 1.2s — conectando)
 *   paired / transcribing      → green (KR verde 1.8s — ativo estável)
 *   recording                  → green-rec (KR verde 0.6s — gravando)
 *   disconnected               → red (KR vermelho 0.9s — clica pra reconectar)
 *   error                      → red (KR vermelho — algo deu ruim)
 */
export function mapVoiceStateToLed(state: VoiceState, locked: boolean): LedVariant {
  if (locked) return 'off';
  switch (state) {
    case 'idle':
    case 'tier_blocked':
      return 'off';
    case 'requesting':
    case 'waiting':
      return 'amber';
    case 'paired':
    case 'transcribing':
      return 'green';
    case 'recording':
      return 'green-rec';
    case 'disconnected':
    case 'error':
      return 'red';
    default:
      return 'off';
  }
}

const VoiceButton = forwardRef<HTMLButtonElement, VoiceButtonProps>(
  ({ tier, active, voiceState = 'idle', onClick, t }, ref) => {
    const locked = shouldShowLock(tier);
    const tooltip = t(getVoiceButtonTooltipKey(tier, active));

    // Estilo base — kit Claude Design (repouso limpo, neon só no estado ativo/hover)
    const baseStyle: CSSProperties = {
      fontFamily: 'var(--font-sans)',
      fontSize: '12px',
      fontWeight: 600,
      padding: '0 14px',
      background: 'transparent',
      border: '1px solid rgba(124, 124, 248, 0.30)',
      color: 'var(--ink-light)',
      cursor: 'pointer',
      letterSpacing: '0.01em',
      textShadow: 'none',
      boxShadow: 'none',
      transition:
        'border-color 180ms ease-out, box-shadow 180ms ease-out, text-shadow 180ms ease-out, color 180ms ease-out',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '46px',
      minWidth: '104px',
      boxSizing: 'border-box',
      position: 'relative',
      gap: '5px',
    };

    let style: CSSProperties = baseStyle;
    if (locked) {
      // Borda 2px mantida pra consistência, glow desligado + cor apagada = "bloqueado"
      style = {
        ...baseStyle,
        border: '1px solid rgba(124,124,248,0.25)',
        boxShadow: 'none',
        color: 'rgba(199,200,206,0.5)',
        textShadow: 'none',
      };
    } else if (active) {
      // Sessão ativa = estado "vivo" → neon verde permitido (não é repouso)
      style = {
        ...baseStyle,
        color: '#FFFFFF',
        border: '1px solid var(--green-fluor)',
        boxShadow: '0 0 14px rgba(0, 255, 65, 0.85), 0 0 32px rgba(0, 255, 65, 0.45)',
        textShadow: '0 0 8px rgba(0, 255, 65, 0.6)',
      };
    }

    // LED variant — mapeia voiceState pro Knight Rider correto.
    // (22/05/2026: state machine completa wired, ver mapVoiceStateToLed)
    const ledVariant: LedVariant = mapVoiceStateToLed(voiceState, locked);

    // Style com border-color baseado em LED variant — coral pra disconnected
    // (urgência), verde pra ativo, amarelo pra connecting (vai sobrescrever
    // o border do baseStyle/activeStyle).
    let dynamicBorderStyle: CSSProperties = {};
    if (ledVariant === 'red') {
      dynamicBorderStyle = {
        border: '2px solid #DC2626',
        boxShadow: '0 0 14px rgba(220, 38, 38, 0.85), 0 0 32px rgba(220, 38, 38, 0.45)',
      };
    } else if (ledVariant === 'amber') {
      dynamicBorderStyle = {
        border: '2px solid #FFC107',
        boxShadow: '0 0 14px rgba(255, 193, 7, 0.85), 0 0 32px rgba(255, 193, 7, 0.45)',
      };
    }

    return (
      <button
        ref={ref}
        type="button"
        className="ia-hdr-btn"
        data-onboarding="header-voice"
        onClick={onClick}
        aria-label={tooltip}
        aria-pressed={active}
        title={tooltip}
        style={{ ...style, ...dynamicBorderStyle }}
      >
        {/* Ícone retro — 🔒 travado, ◎ paired, ⏵ recording, ⚡ disconnected, 🎙 idle */}
        <span aria-hidden="true" style={{ fontSize: locked ? 15 : 16, lineHeight: 1 }}>
          {locked
            ? '🔒'
            : voiceState === 'disconnected' || voiceState === 'error'
            ? '⚡'
            : voiceState === 'recording'
            ? '⏵'
            : active
            ? '◎'
            : '🎙'}
        </span>
        <span>{t('header.voice')}</span>
        <LedPanel variant={ledVariant} />
      </button>
    );
  },
);

VoiceButton.displayName = 'VoiceButton';

export default VoiceButton;
